import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { enhancedApi as firmsApi } from '@/services/api/generated/firmsService';
import {
  type SmsTemplateCategory,
  type SmsTemplateSchema,
  useGlobalSmsTemplatesCreateMutation,
  useGlobalSmsTemplatesPartialUpdateMutation,
} from '@/services/api/generated/smsTemplatesService';

import { TemplateVariableReference } from './TemplateVariableReference';

const CATEGORIES: { value: SmsTemplateCategory; label: string }[] = [
  { value: 'Document Collection', label: 'Document Collection' },
  {
    value: 'Initial Treatment Assessment',
    label: 'Initial Treatment Assessment',
  },
  { value: 'Ongoing Treatment Check-In', label: 'Ongoing Treatment Check-In' },
];

type TemplateScope = 'global' | 'firm';

interface SmsTemplateEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: SmsTemplateSchema | null;
  onSaved?: () => void;
}

export const SmsTemplateEditor = ({
  open,
  onOpenChange,
  template,
  onSaved,
}: SmsTemplateEditorProps) => {
  const isEditing = !!template;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [name, setName] = useState('');
  const [category, setCategory] = useState<SmsTemplateCategory>(
    'Ongoing Treatment Check-In',
  );
  const [content, setContent] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [priority, setPriority] = useState<string>('');
  const [cadenceDay, setCadenceDay] = useState<string>('');
  const [templateScope, setTemplateScope] = useState<TemplateScope>('global');
  const [selectedFirmId, setSelectedFirmId] = useState<string>('');

  // Fetch firms list
  const { data: firmsList } = firmsApi.useFirmsListQuery();

  // Global template mutations
  const [createGlobalTemplate, { isLoading: isCreatingGlobal }] =
    useGlobalSmsTemplatesCreateMutation();
  const [updateGlobalTemplate, { isLoading: isUpdatingGlobal }] =
    useGlobalSmsTemplatesPartialUpdateMutation();

  // Firm template mutations
  const [createFirmTemplate, { isLoading: isCreatingFirm }] =
    firmsApi.useFirmSmsTemplatesCreateMutation();
  const [updateFirmTemplate, { isLoading: isUpdatingFirm }] =
    firmsApi.useFirmSmsTemplatesPartialUpdateMutation();

  const isLoading =
    isCreatingGlobal || isUpdatingGlobal || isCreatingFirm || isUpdatingFirm;

  // Populate form when editing
  useEffect(() => {
    if (open && template) {
      setName(template.name);
      setCategory(template.category);
      setContent(template.content);
      setIsActive(template.is_active);
      setIsDefault(template.is_default ?? false);
      setPriority(template.priority?.toString() ?? '');
      setCadenceDay(template.cadence_day?.toString() ?? '');
      // Set scope based on whether template has a firm_id
      if (template.firm_id) {
        setTemplateScope('firm');
        setSelectedFirmId(template.firm_id);
      } else {
        setTemplateScope('global');
        setSelectedFirmId('');
      }
    } else if (open && !template) {
      // Reset form for new template
      setName('');
      setCategory('Ongoing Treatment Check-In');
      setContent('');
      setIsActive(true);
      setIsDefault(false);
      setPriority('');
      setCadenceDay('');
      setTemplateScope('global');
      setSelectedFirmId('');
    }
  }, [open, template]);

  const handleInsertVariable = useCallback((variable: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setContent((prev) => `${prev}{{${variable}}}`);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const insertion = `{{${variable}}}`;

    setContent(
      (prev) => `${prev.slice(0, start)}${insertion}${prev.slice(end)}`,
    );

    // Restore focus and cursor position after insertion
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + insertion.length,
        start + insertion.length,
      );
    });
  }, []);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    if (!content.trim()) {
      toast.error('Please enter template content');
      return;
    }
    if (templateScope === 'firm' && !selectedFirmId) {
      toast.error('Please select a firm for the template');
      return;
    }

    const isFirmTemplate = templateScope === 'firm' && !!selectedFirmId;

    try {
      if (isEditing && template) {
        // Update existing template
        if (isFirmTemplate) {
          await updateFirmTemplate({
            firmId: selectedFirmId,
            id: template.id as string,
            patchedUpdateSmsTemplateRequest: {
              name: name.trim(),
              content: content.trim(),
              is_active: isActive,
              is_default: isDefault,
              priority: priority ? parseInt(priority, 10) : null,
              cadence_day: cadenceDay ? parseInt(cadenceDay, 10) : null,
            },
          }).unwrap();
        } else {
          await updateGlobalTemplate({
            id: template.id as string,
            patchedUpdateSmsTemplateRequest: {
              name: name.trim(),
              content: content.trim(),
              is_active: isActive,
              is_default: isDefault,
              priority: priority ? parseInt(priority, 10) : null,
              cadence_day: cadenceDay ? parseInt(cadenceDay, 10) : null,
            },
          }).unwrap();
        }
        toast.success('Template updated');
      } else if (isFirmTemplate) {
        // Create new firm template
        await createFirmTemplate({
          firmId: selectedFirmId,
          createSmsTemplateRequest: {
            name: name.trim(),
            category,
            content: content.trim(),
            is_active: isActive,
            is_default: isDefault,
            priority: priority ? parseInt(priority, 10) : undefined,
            cadence_day: cadenceDay ? parseInt(cadenceDay, 10) : null,
          },
        }).unwrap();
        toast.success('Firm template created');
      } else {
        // Create new global template
        await createGlobalTemplate({
          createSmsTemplateRequest: {
            name: name.trim(),
            category,
            content: content.trim(),
            is_active: isActive,
            is_default: isDefault,
            priority: priority ? parseInt(priority, 10) : undefined,
            cadence_day: cadenceDay ? parseInt(cadenceDay, 10) : null,
          },
        }).unwrap();
        toast.success('Global template created');
      }

      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to save template:', err);
      toast.error(
        isEditing ? 'Failed to update template' : 'Failed to create template',
      );
    }
  };

  const dialogTitle = isEditing ? 'Edit Template' : 'Create Template';

  // Get firm name for display when editing a firm template
  const selectedFirmName = firmsList?.find(
    (f) => f.id === selectedFirmId,
  )?.name;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Scope selector - only show when creating new templates */}
          {!isEditing && (
            <div className="space-y-1.5">
              <Label>Template Scope</Label>
              <Select
                value={templateScope}
                onValueChange={(v) => {
                  setTemplateScope(v as TemplateScope);
                  if (v === 'global') {
                    setSelectedFirmId('');
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">
                    Global (available to all firms)
                  </SelectItem>
                  <SelectItem value="firm">Firm-specific</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Firm selector - show when scope is firm */}
          {templateScope === 'firm' && !isEditing && (
            <div className="space-y-1.5">
              <Label>Firm</Label>
              <Select value={selectedFirmId} onValueChange={setSelectedFirmId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a firm..." />
                </SelectTrigger>
                <SelectContent>
                  {firmsList?.map((firm) => (
                    <SelectItem key={firm.id} value={firm.id}>
                      {firm.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Show firm name when editing a firm template */}
          {isEditing && templateScope === 'firm' && selectedFirmName && (
            <div className="rounded-lg border bg-zinc-50 p-3">
              <p className="text-xs text-zinc-500">
                Firm-specific template for
              </p>
              <p className="text-sm font-medium text-zinc-900">
                {selectedFirmName}
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="name">Template Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Treatment Check-In Reminder"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="category">Category</Label>
            <Select
              value={category}
              onValueChange={(v) => setCategory(v as SmsTemplateCategory)}
              disabled={isEditing}
            >
              <SelectTrigger id="category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isEditing && (
              <p className="text-xs text-zinc-400">
                Category cannot be changed after creation
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="content">Message Template</Label>
              <TemplateVariableReference onInsert={handleInsertVariable} />
            </div>
            <Textarea
              ref={textareaRef}
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Hi {{clientFirstName}}, this is a reminder about..."
              className="min-h-[120px]"
            />
            <p className="text-xs text-zinc-400">{content.length} characters</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="priority">Priority</Label>
              <Input
                id="priority"
                type="number"
                min="1"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                placeholder="e.g., 100"
              />
              <p className="text-xs text-zinc-400">
                Lower values are recommended first. Default templates take
                precedence, then sorted by priority.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cadenceDay">Cadence Day</Label>
              <Input
                id="cadenceDay"
                type="number"
                min="0"
                value={cadenceDay}
                onChange={(e) => setCadenceDay(e.target.value)}
                placeholder="e.g., 7"
              />
              <p className="text-xs text-zinc-400">
                Days since client last responded. The system selects the highest
                cadence day that doesn&apos;t exceed actual days. Leave empty
                for fallback.
              </p>
            </div>
          </div>

          <div className="space-y-2 pt-1">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isActive"
                checked={isActive}
                onCheckedChange={(checked) => setIsActive(checked === true)}
              />
              <Label htmlFor="isActive" className="cursor-pointer font-normal">
                Active (available for use)
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="isDefault"
                checked={isDefault}
                onCheckedChange={(checked) => setIsDefault(checked === true)}
              />
              <Label htmlFor="isDefault" className="cursor-pointer font-normal">
                Default template for this category
              </Label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {isEditing ? 'Save Changes' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
